/** Date-only helpers — avoid `new Date('YYYY-MM-DD')` UTC midnight off-by-one. */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Normalize API/form values to YYYY-MM-DD (calendar day, no TZ shift). */
export function toDateOnlyString(value?: string | Date | null): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  const m = s.match(DATE_ONLY_RE);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return '';
  return toDateOnlyString(dt);
}

/** Parse YYYY-MM-DD (or ISO prefix) as local midnight Date. */
export function parseDateOnly(value?: string | Date | null): Date | null {
  const day = toDateOnlyString(value);
  if (!day) return null;
  const dt = new Date(`${day}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Display as dd/mm/yyyy (pt-BR) without UTC day shift. */
export function formatDateOnly(value?: string | Date | null): string {
  const day = toDateOnlyString(value);
  if (!day) return '-';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

/** Today's calendar date in local TZ as YYYY-MM-DD. */
export function todayDateOnly(): string {
  return toDateOnlyString(new Date());
}

/** Typing mask DD/MM/YYYY (digits only). */
export function applyDateBrMask(value: string): string {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Parse DD/MM/YYYY → YYYY-MM-DD, or null if incomplete/invalid calendar day. */
export function parseDateBrToIso(br?: string | null): string | null {
  const s = String(br || '').trim();
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const iso = `${m[3]}-${m[2]}-${m[1]}`;
  if (formatDateOnly(iso) !== s) return null;
  return iso;
}

/** YYYY-MM-DD → DD/MM/YYYY for controlled inputs (empty if missing). */
export function formatIsoToDateBr(value?: string | Date | null): string {
  const formatted = formatDateOnly(value);
  return formatted === '-' ? '' : formatted;
}
